import { useEffect } from "react";
import { Footer } from "@/components/lunar/Footer";
import { Logo } from "@/components/lunar/Logo";
import { Reveal } from "@/components/lunar/Reveal";

const SECTIONS = [
    {
        title: "Our standards",
        paragraphs: [
            "Lunar is committed to maintaining a safe platform and has zero tolerance for child sexual abuse and exploitation (CSAE) or child sexual abuse material (CSAM).",
            "Lunar is intended exclusively for adults aged 18 and over. Users must confirm that they are at least 18 when creating an account.",
            "We prohibit any content or behaviour that sexually exploits, abuses or endangers children, including grooming, sexual exploitation of minors, sextortion, trafficking for sexual purposes, or the creation, sharing, solicitation or distribution of CSAM.",
        ],
    },
    {
        title: "Reporting and enforcement",
        paragraphs: [
            "Users can report inappropriate users, content or behaviour directly within the Lunar app. Reports relating to child safety are treated seriously and may result in content removal, account suspension or termination and other appropriate action.",
            "Where we become aware of CSAM or other child sexual exploitation, Lunar will take appropriate action in accordance with applicable law, including reporting confirmed illegal material or conduct to the appropriate regional or national authorities where required.",
            "Lunar cooperates with law enforcement and relevant child-safety authorities where legally required.",
        ],
    },
];

export default function ChildSafety() {
    useEffect(() => {
        document.title = "Child safety standards — Lunar";
        return () => {
            document.title = "Lunar — The social network for real life";
        };
    }, []);

    return (
        <div data-testid="child-safety-page">
            <header className="mx-auto flex max-w-6xl items-center justify-between px-6 pt-7">
                <a href="/" aria-label="Back to Lunar home" data-testid="child-safety-back-home">
                    <Logo size={38} />
                </a>
                <a
                    href="/"
                    data-testid="child-safety-home-link"
                    className="rounded-full border border-[#C2C0B8] px-4 py-2 text-xs font-medium text-[#3E413B] transition-[border-color,color,transform] duration-300 hover:-translate-y-[1px] hover:border-[#899B6A] hover:text-[#2C3618]"
                >
                    Home
                </a>
            </header>

            <main className="mx-auto max-w-4xl px-6 pb-24 pt-16 sm:pt-20">
                <Reveal>
                    <p className="text-[11px] font-medium uppercase tracking-[0.28em] text-[#899B6A]">
                        Safety standards
                    </p>
                    <h1
                        data-testid="child-safety-heading"
                        className="font-display mt-5 text-4xl font-medium leading-[1.08] tracking-tight text-[#1D201A] sm:text-6xl"
                    >
                        Child safety standards
                    </h1>
                </Reveal>

                <div className="mt-14 space-y-5">
                    {SECTIONS.map((section, i) => (
                        <Reveal key={section.title} delay={i * 0.08}>
                            <section
                                data-testid={`child-safety-section-${i + 1}`}
                                className="rounded-3xl border border-[#E5E3DC] bg-[#F9F8F4] p-7 sm:p-9"
                            >
                                <h2 className="font-display text-3xl font-medium tracking-tight text-[#1D201A]">
                                    {section.title}
                                </h2>
                                <div className="mt-5 space-y-4">
                                    {section.paragraphs.map((paragraph) => (
                                        <p key={paragraph} className="text-base leading-relaxed text-[#3E413B]">
                                            {paragraph}
                                        </p>
                                    ))}
                                </div>
                            </section>
                        </Reveal>
                    ))}

                    <Reveal delay={0.18}>
                        <section
                            data-testid="child-safety-report-section"
                            className="rounded-3xl border border-[#C9D1B7] bg-[#EEF1E5] p-7 sm:p-9"
                        >
                            <h2 className="font-display text-3xl font-medium tracking-tight text-[#1D201A]">
                                Report a child safety concern
                            </h2>
                            <p className="mt-5 text-base leading-relaxed text-[#3E413B]">
                                If you become aware of potential child sexual abuse or exploitation
                                involving Lunar, please report it using the reporting tools within the
                                app or contact us at hello@somerhillgroup.com.
                            </p>
                            <a
                                href="mailto:hello@somerhillgroup.com?subject=Lunar%20child%20safety%20concern"
                                data-testid="child-safety-report-link"
                                className="mt-6 inline-flex rounded-full bg-[#2C3618] px-6 py-3 text-sm font-bold text-[#F9F8F4] transition-[background-color,transform] duration-300 hover:-translate-y-[1px] hover:bg-[#4A5D23]"
                            >
                                Report a child safety concern
                            </a>
                            <p className="mt-8 border-t border-[#C2C0B8] pt-5 text-sm text-[#5C5F58]">
                                Lunar is operated by Somerhill Group Ltd.
                            </p>
                        </section>
                    </Reveal>
                </div>
            </main>
            <Footer />
        </div>
    );
}
